/**
 * Synthesis-view bootstrapper.
 *
 * Loads the combined synthesis graph (Top Concepts / Top Graphs), processes
 * its statements, then kicks off an AI-topics fetch in the background.
 *
 * Lifted verbatim from useBootstrapStore Step 4. Behavior is intentionally
 * unchanged — same log lines, same try/catch boundaries, same callback timing.
 */

import {
    fetchSynthesisGraph,
    fetchSynthesisGraphTopics,
} from '../../hooks/useApi'
import { useZustand } from '../../hooks/useZustand'
import { useGraphStore } from '../useGraphStore'
import { useFilterStore } from '../useFilterStore'
import { formatTopicsFromClusters } from '../../utils/graphTopics'
import { processStatements, initTopicsFromGraph } from './processStatements'
import { getGraphPanelFromUrl, getControlsFromUrl } from './urlHelpers'

export const bootstrapSynthesisView = async ({
    synthesisType,
    stopWords,
    graphOwnerUsername,
    isPublic,
    onLoadingStatements,
}) => {
    console.log(
        `[Bootstrap] Step 4: Loading ${synthesisType} synthesis graph...`
    )

    // For anonymous/public visitors, the backend needs `userName` in the
    // request body to resolve the graph owner (the contextsList middleware's
    // anonymous branch reads `req.body.userName`). Logged-in viewers rely on
    // their JWT and omit it.
    const publicUserName = isPublic ? graphOwnerUsername : null

    try {
        useGraphStore.getState().setLoading(true)

        // Re-arm the auto-message gate (`home/index.jsx`): a fresh graph
        // load means the ChatWidget will re-hydrate, so its `onReady`
        // must fire again before `setForceRequestMode` can land.
        useZustand.getState().setIsAiChatReady(false)

        // Seed the graph controls / analytics panel start state from
        // `?controls=`. Null means "let the graph use its own heuristic".
        // Done up-front (not in the AI-topics callback) so the prop is set
        // by the time LazyGraph renders.
        useZustand.getState().setCurrentGraphControlsMode(getControlsFromUrl())

        const graphData = await fetchSynthesisGraph(
            synthesisType,
            stopWords,
            publicUserName
        )
        useGraphStore.getState().setGraphData(graphData)

        // Step 5: Process statements and topics
        console.log('[InfraNodus] Step 5: Processing statements')
        if (onLoadingStatements) onLoadingStatements()

        const statements = useGraphStore.getState().getStatements()
        if (statements && statements.length > 0) {
            // Synthesis has no single "current context" — pass null
            // and let processStatements group by each statement's
            // actual contextId. The filter panel flattens for synthesis.
            await processStatements({
                statements,
                contextName: null,
                addContexts: [],
                isSynthesis: true,
            })
        }

        // Set initial fallback topics from graph clusters
        initTopicsFromGraph()

        // Fetch AI topic names in the background
        const graphDataSnapshot = useGraphStore.getState().graphData
        fetchSynthesisGraphTopics(synthesisType, stopWords, publicUserName)
            .then(topicsExtension => {
                if (!topicsExtension) return

                // Bail if graph changed while we were fetching
                if (useGraphStore.getState().graphData !== graphDataSnapshot) {
                    return
                }

                useGraphStore.getState().applyTopicsExtension(topicsExtension)

                const updatedClusters =
                    useGraphStore.getState().graphData?.entriesAndGraphOfContext
                        ?.graph?.graphologyGraph?.attributes?.top_clusters || []

                if (updatedClusters.length > 0) {
                    const formattedTopics = formatTopicsFromClusters(
                        updatedClusters
                    )
                    useZustand.getState().setTopics(formattedTopics, true)
                    // Honor `?graph_panel=` (or mapped legacy `?panel=`) so
                    // share/embed links can land on a non-default layer.
                    useZustand
                        .getState()
                        .setCurrentGraphMode(getGraphPanelFromUrl() || 'topics')
                    useFilterStore
                        .getState()
                        .updateFilteredStatementsWithCommunities()
                }
            })
            .catch(err => {
                console.warn('[Bootstrap] Synthesis AI topics failed:', err)
            })
    } catch (err) {
        console.error('[Bootstrap] Failed to load synthesis graph:', err)
        useGraphStore.getState().setError(err.message)
    }
}
